const {KitModule, registerModule} = window;
const {
  buildExactQuery,
  escapeHtml,
  hasSearchTokens,
  mergeSearchResults,
  rankSemanticMatches,
} = require('./searchRanking.util');

class Search extends KitModule {
  constructor($element) {
    super($element);

    this.setVars();
    this.bindEvents();
  }

  setVars() {
    this.pagefind = undefined;
    this.pagefindPromise = undefined;
    this.timer = 0;
    this.loading = false;
    this.debounceTimeoutMs = 300;
    this.searchTerm = '';
    this.searchId = 0;
    this.searchResult = {results: []};
    this.resultDataCache = new Map();
    this.semanticRankingTimer = 0;
    this.semanticRankingBatchSize = 10;
    this.pageSize = Number(this.$searchResult.getAttribute('data-pagefind-size')) || 5;
    this.show = this.pageSize;

    // options
    this.includeMedia = this.$searchResult.hasAttribute('data-pagefind-include-media');
    this.$modal = this.$element.closest('#modal-search');
  }

  bindEvents() {
    this.$searchInput.addEventListener('input', this);
    this.$searchInput.addEventListener('focus', this);
    this.$searchInput.addEventListener('keydown', this);

    this.$showMore.addEventListener('click', this.onShowMore.bind(this));
    this.$clearInput.addEventListener('click', this.onClearInput.bind(this));
    this.$result.addEventListener('click', this.onResultClick.bind(this));

    // listen for searchToggle state
    window.addEventListener('searchopen', this);
    window.addEventListener('searchclose', this);

    if (this.$modal) {
      this.$modal.addEventListener('toggle', this);
    }
  }

  onkeydown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
    } else if (e.key === 'Escape') {
      this.onsearchclose();
    }
  }

  ontoggle(event) {
    if (event.newState === 'closed') {
      // Notify GTM tracking with the final search term before clearing
      if (this.searchTerm) {
        window.dispatchEvent(new CustomEvent('gtm:search', {detail: {searchTerm: this.searchTerm}}));
      }
      this.clearAll();
    } else if (event.newState === 'open') {
      this.init().catch(() => {});
      this.$searchInput.value = '';
      this.$searchInput.focus();
    }
  }

  onsearchopen() {
    this.$element.classList.add('is-visible');
    this.init().catch(() => {});
    this.$searchInput.value = '';
    this.$searchInput.focus();
  }

  onsearchclose() {
    // Notify GTM tracking with the final search term before clearing
    if (this.searchTerm) {
      window.dispatchEvent(new CustomEvent('gtm:search', {detail: {searchTerm: this.searchTerm}}));
    }
    this.$element.classList.remove('is-visible');
    this.clearAll();
    this.dispatchEvent('searchescape');
  }

  onClearInput(e) {
    e.preventDefault();
    this.$searchInput.focus();
    this.clearAll();
  }

  onResultClick(e) {
    if (e.target.closest('a') && this.searchTerm) {
      window.dispatchEvent(new CustomEvent('gtm:search', {detail: {searchTerm: this.searchTerm}}));
      // Clear searchTerm so we don't fire it again if onsearchclose runs during navigation
      this.searchTerm = '';
    }
  }

  onfocus() {
    this.init().catch(() => {});
  }

  clearAll() {
    if (this.timer) clearTimeout(this.timer);
    if (this.semanticRankingTimer) clearTimeout(this.semanticRankingTimer);
    this.timer = 0;
    this.semanticRankingTimer = 0;
    this.searchId += 1;
    this.$searchInput.value = '';
    this.$result.innerHTML = '';
    this.$message.innerHTML = '';
    this.searchResult = {results: []};
    this.resultDataCache.clear();
    this.$showMore.classList.remove('is-visible');
    this.$message.classList.remove('is-visible');
  }

  oninput() {
    const term = this.$searchInput.value;

    if (!term.trim()) {
      this.clearAll();
      return;
    }

    this.debouncedSearch(term);
  }

  async getResult(searchId) {
    this.$result.innerHTML = '';
    const results = this.searchResult.results.slice(0, this.show);
    await this.renderResult(results, searchId);
  }

  renderHtml(data) {
    const meta = data.meta || {};
    const url = escapeHtml(data.url);
    let output = `<li class="search__results-item">
        <a href="${url}" class="search-result__content">`;

    if (meta.image && this.includeMedia) {
      output += `<div class="search__results__media">
                <div class="frame frame--1-1">
                    <img src="${escapeHtml(meta.image)}" alt="${escapeHtml(meta.image_alt)}"/>
                </div>
            </div>`;
    }

    output += '<div class="search__results__header"><div class="search__results-metas">';

    if (meta.tags) {
      const tags = meta.tags.split(',');
      output += '<div class="tags">';

      tags.forEach((tag) => {
        output += `<div class="tag | type-eyebrow-1">${escapeHtml(tag)}</div>`;
      });

      output += '</div>';
    }

    if (meta.tags && meta.category) {
      output += '<span>-</span>';
    }

    if (meta.category) {
      output += `<p class="type-body-1">${escapeHtml(meta.category)}</p>`;
    }

    const type = escapeHtml(meta['post-type'] || 'Page');
    const title = escapeHtml(meta.title);

    output += `</div>
                <p class="eyebrow type-eyebrow-1 u-mb--space-fixed-2">${type}</p>
                <h2 class="type-subhead-1 u-mb--space-fixed-2">${title}</h2>
                <p class="type-body-1">${data.excerpt}</p>
              </div>
            </a>
        </li>`;

    return output;
  }

  showHideMoreButton() {
    if (this.searchResult.results.length > this.show) {
      this.$showMore.classList.add('is-visible');
    } else {
      this.$showMore.classList.remove('is-visible');
    }
  }

  onShowMore(e) {
    e.preventDefault();
    const current = this.show;
    this.show += this.pageSize;
    this.loadMore(current);
  }

  async loadMore(current) {
    const results = this.searchResult.results.slice(current, this.show);

    await this.renderResult(results, this.searchId, true);
    if (!this.loading) this.showHideMoreButton();
  }

  getResultData(result) {
    if (!this.resultDataCache.has(result.id)) {
      this.resultDataCache.set(result.id, result.data());
    }

    return this.resultDataCache.get(result.id);
  }

  async getResultDataInBatches(results, searchId) {
    const resultData = [];

    for (let index = 0; index < results.length; index += this.semanticRankingBatchSize) {
      const batch = results.slice(index, index + this.semanticRankingBatchSize);
      const batchData = await Promise.all(batch.map((result) => this.getResultData(result)));
      if (searchId !== this.searchId) return null;

      resultData.push(...batchData);

      if (index + this.semanticRankingBatchSize < results.length) {
        await new Promise((resolve) => setTimeout(resolve, 25));
        if (searchId !== this.searchId) return null;
      }
    }

    return resultData;
  }

  hasResultOrderChanged(previousResults, nextResults) {
    return previousResults.length !== nextResults.length ||
      previousResults.some((result, index) => result.id !== nextResults[index]?.id);
  }

  scheduleSemanticRanking(term, searchId, semanticResults) {
    if (!semanticResults.length) return;

    if (this.semanticRankingTimer) clearTimeout(this.semanticRankingTimer);

    this.semanticRankingTimer = setTimeout(() => {
      this.semanticRankingTimer = 0;
      this.prioritizeSemanticMatches(term, searchId, semanticResults).catch(() => {});
    }, 50);
  }

  async prioritizeSemanticMatches(term, searchId, semanticResults) {
    const semanticData = await this.getResultDataInBatches(semanticResults, searchId);
    if (!semanticData || searchId !== this.searchId) return;

    const rankedSemanticResults = rankSemanticMatches(term, semanticResults, semanticData);
    if (!this.hasResultOrderChanged(semanticResults, rankedSemanticResults)) return;

    // Keep exact title/H1 matches ahead without blocking the first visible result batch.
    this.searchResult = mergeSearchResults({results: rankedSemanticResults}, this.searchResult);
    await this.getResult(searchId);
    if (searchId !== this.searchId) return;

    this.showHideMoreButton();
  }

  async renderResult(results, searchId, append = false) {
    const resultData = await Promise.all(results.map((result) => this.getResultData(result)));
    if (searchId !== this.searchId) return;

    const resultHtml = resultData.map((data) => this.renderHtml(data)).join('');
    if (append) {
      this.$result.insertAdjacentHTML('beforeend', resultHtml);
    } else {
      this.$result.innerHTML = resultHtml;
    }

    this.displayMessage(this.searchResult.results.length);
  }

  displayMessage(totalResults) {
    const resultString = totalResults === 1 ? 'result' : 'results';

    this.$message.classList.add('is-visible');
    this.$message.innerText = `${totalResults} ${resultString} for "${this.$searchInput.value}"`;
  }

  dispatchEvent(eventName) {
    const event = new Event(eventName);
    window.dispatchEvent(event);
  }

  async search(term, localSearchId = ++this.searchId) {
    const normalizedTerm = term.trim();
    this.searchTerm = normalizedTerm;
    this.loading = true;
    this.timer = 0;
    this.resultDataCache.clear();
    this.$message.classList.add('is-visible');
    this.$message.innerText = 'Searching...';

    if (!hasSearchTokens(normalizedTerm)) {
      this.loading = false;
      this.searchResult = {results: []};
      this.$result.innerHTML = '';
      this.$showMore.classList.remove('is-visible');
      this.$message.innerText = 'Please enter a more specific search term.';
      return;
    }

    try {
      const pagefind = await this.init();
      const exactQuery = buildExactQuery(normalizedTerm);
      const [exactResults, relevanceResults] = await Promise.all([
        exactQuery ? pagefind.search(exactQuery) : Promise.resolve({results: []}),
        pagefind.search(normalizedTerm),
      ]);

      if (this.searchId !== localSearchId) return;

      // Exact title/H1 matches are intentional navigational results, before body-only matches.
      this.searchResult = mergeSearchResults(exactResults, relevanceResults);
      this.show = this.pageSize;
      this.loading = false;
      await this.getResult(localSearchId);
      if (this.searchId !== localSearchId) return;
      this.showHideMoreButton();
      this.scheduleSemanticRanking(normalizedTerm, localSearchId, mergeSearchResults(exactResults).results);
    } catch (error) {
      if (this.searchId !== localSearchId) return;

      this.loading = false;
      this.$result.innerHTML = '';
      this.$showMore.classList.remove('is-visible');
      this.$message.classList.add('is-visible');
      this.$message.innerText = 'Search is temporarily unavailable. Please try again.';
      console.error('Pagefind search failed', error); // eslint-disable-line no-console
    }
  }

  debouncedSearch(term) {
    const normalizedTerm = term.trim();
    const localSearchId = ++this.searchId;
    const executeSearchFunc = () => this.search(normalizedTerm, localSearchId);

    if (this.debounceTimeoutMs > 0) {
      if (this.timer) clearTimeout(this.timer);
      this.timer = setTimeout(executeSearchFunc, this.debounceTimeoutMs);
    } else {
      executeSearchFunc();
    }

    // Start downloading the shared term chunks during the debounce window.
    if (!hasSearchTokens(normalizedTerm)) return;

    this.init()
      .then((pagefind) => pagefind.preload(normalizedTerm))
      .catch(() => {});
  }

  async init() {
    if (this.pagefind) return this.pagefind;
    if (this.pagefindPromise) return this.pagefindPromise;

    this.pagefindPromise = import('/pagefind/pagefind.js')
      .then(async (pagefind) => {
        await pagefind.init();
        // Page titles should rank alongside the site's primary H1 headings.
        await pagefind.options({ranking: {metaWeights: {title: 10}}});
        this.pagefind = pagefind;
        return pagefind;
      })
      .catch((error) => {
        this.pagefindPromise = undefined;
        console.error('Pagefind couldn\'t be loaded', error); // eslint-disable-line no-console
        throw error;
      });

    return this.pagefindPromise;
  }
}


registerModule('Search', Search);
